import { Button, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { DisabledDot, EnabledDot } from '@/client/components/icons/ContinuIcons';

import ExpertsColorChangeIcon from './ExpertsColorChangeIcon';
import Loading from '@/client/components/media/Loading';
import NoExpertsDashboardPlaceholder from './experts-dashboard/NoExpertsDashboardPlaceholder';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { useCheckFeatureFlag } from '@/client/services/hooks/auth/useCheckFeatureFlag';
import { useExpertsStore } from './expertsStore';
import { useListExperts } from './hooks/useListExperts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ExpertsPreviewList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsEditingExpert } = useExpertsStore();
  const expertsEnabled = useCheckFeatureFlag('enable_ai_eddy_experts');

  const { data, isLoading, isError } = useListExperts({ pageSize: 4 });

  if (isLoading) return <Loading />;

  if (isError) return <div>Error fetching experts</div>;

  return (
    <Stack spacing={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="22px" fontWeight={600} color="baseBlack">
          {t('admin.aiAgent.experts.previewList.title')}
        </Text>

        {data.edges.length > 3 && expertsEnabled && (
          <Button variant="ghost" onClick={() => navigate('experts')}>
            {t('admin.aiAgent.experts.previewList.viewAll')}
          </Button>
        )}
      </Flex>

      {(data.edges.length === 0 || !expertsEnabled) && <NoExpertsDashboardPlaceholder />}

      {data.edges.length > 0 && expertsEnabled && (
        <HStack spacing={4} width="full">
          {[...data.edges]
            .sort((a, b) => Number(b.node.enabled) - Number(a.node.enabled))
            .map((expert) => {
              const { node } = expert;
              const { id, name, enabled, color } = node;

              return (
                <Flex
                  key={id}
                  backgroundColor="neutral.25"
                  border="1px solid"
                  borderColor="neutral.100"
                  borderRadius="24px"
                  paddingX={6}
                  paddingY={8}
                  width="25%"
                  maxWidth="25%"
                  height="250px"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <Stack spacing={4} marginBottom={12}>
                    <ExpertsColorChangeIcon color={color} />

                    <Text
                      fontSize="16px"
                      fontWeight={600}
                      color="baseBlack"
                      noOfLines={1}
                      textOverflow="ellipsis"
                      whiteSpace="wrap"
                    >
                      {name}
                    </Text>

                    <HStack spacing={1}>
                      <Icon as={enabled ? EnabledDot : DisabledDot} />

                      <Text
                        fontSize="14px"
                        fontWeight={600}
                        color={enabled ? '#00AF4F' : 'neutral.500'}
                      >
                        {enabled ? t('global.enabled') : t('global.disabled')}
                      </Text>
                    </HStack>
                  </Stack>

                  <OutlineButton
                    width="fit-content"
                    onClick={() => {
                      setIsEditingExpert(true);
                      navigate(`/admin/eddy/experts/details/${id}`);
                    }}
                  >
                    {t('admin.aiAgent.experts.previewList.manage')}
                  </OutlineButton>
                </Flex>
              );
            })}
        </HStack>
      )}
    </Stack>
  );
}
